import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { CONTACT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQUIREMENT_OPTIONS = [
  "Trigger — Municipal Controller",
  "Macro — Borewell Controller",
  "Matrix — Multi-Tank (Industrial)",
  "Flexibell — Water Level Alarm",
  "AMC / Service",
  "Dealer / Partnership",
  "General",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    company: "",
    requirement_type: "General",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSuccess(true);
      toast.success("Inquiry received — we'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", city: "", company: "", requirement_type: "General", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid={CONTACT.root} className="border-b border-zinc-200">
      <div className="px-6 md:px-12 lg:px-24 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#002FA7] mb-4">
            /// Get in Touch
          </div>
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-tight">
            Talk to a water automation engineer.
          </h2>
          <p className="mt-6 text-zinc-700 text-lg">
            Send a brief — we&apos;ll respond within one business day with a suggested BOQ, ballpark
            cost and a free on-site survey slot.
          </p>

          <div className="mt-10 space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 border border-zinc-300 flex items-center justify-center shrink-0">
                <Phone className="w-4 h-4 text-[#002FA7]" />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Toll-free</div>
                <div className="font-medium mt-1">1800 891 2595</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 border border-zinc-300 flex items-center justify-center shrink-0">
                <Mail className="w-4 h-4 text-[#002FA7]" />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Email</div>
                <div className="font-medium mt-1">aquamaticsales@gmail.com</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 border border-zinc-300 flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 text-[#002FA7]" />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">HQ</div>
                <div className="font-medium mt-1">
                  Plot No. 649, Industrial Area Phase IX,<br />
                  Mohali, SAS Nagar, Punjab
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid={CONTACT.form}
            className="border border-zinc-300 bg-white p-6 md:p-10 shadow-hard"
          >
            {success && (
              <div
                data-testid={CONTACT.successAlert}
                className="mb-6 border-l-4 border-[#002FA7] bg-[#EFF6FF] px-4 py-3 text-sm"
              >
                Thank you — your inquiry has been received. Our team will reach out shortly.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Name *">
                <Input
                  data-testid={CONTACT.name}
                  value={form.name}
                  onChange={onChange("name")}
                  required
                  className="rounded-none border-zinc-300 focus-visible:ring-[#002FA7]"
                />
              </Field>
              <Field label="Email *">
                <Input
                  type="email"
                  data-testid={CONTACT.email}
                  value={form.email}
                  onChange={onChange("email")}
                  required
                  className="rounded-none border-zinc-300 focus-visible:ring-[#002FA7]"
                />
              </Field>
              <Field label="Phone *">
                <Input
                  data-testid={CONTACT.phone}
                  value={form.phone}
                  onChange={onChange("phone")}
                  required
                  className="rounded-none border-zinc-300 focus-visible:ring-[#002FA7]"
                />
              </Field>
              <Field label="City">
                <Input
                  data-testid={CONTACT.city}
                  value={form.city}
                  onChange={onChange("city")}
                  className="rounded-none border-zinc-300 focus-visible:ring-[#002FA7]"
                />
              </Field>
              <Field label="Company / Society">
                <Input
                  data-testid={CONTACT.company}
                  value={form.company}
                  onChange={onChange("company")}
                  className="rounded-none border-zinc-300 focus-visible:ring-[#002FA7]"
                />
              </Field>
              <Field label="Requirement">
                <Select
                  value={form.requirement_type}
                  onValueChange={(v) => setForm({ ...form, requirement_type: v })}
                >
                  <SelectTrigger
                    data-testid={CONTACT.requirement}
                    className="rounded-none border-zinc-300 focus:ring-[#002FA7]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {REQUIREMENT_OPTIONS.map((r) => (
                      <SelectItem key={r} value={r} className="rounded-none">
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Tell us about your project *">
                <Textarea
                  data-testid={CONTACT.message}
                  value={form.message}
                  onChange={onChange("message")}
                  rows={5}
                  required
                  placeholder="Number of tanks, pump ratings, building type, existing pain points..."
                  className="rounded-none border-zinc-300 focus-visible:ring-[#002FA7]"
                />
              </Field>
            </div>

            <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
              <p className="text-xs text-zinc-500 font-mono uppercase tracking-widest">
                We reply within 1 business day
              </p>
              <Button
                type="submit"
                disabled={loading}
                data-testid={CONTACT.submit}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-none font-mono uppercase tracking-wider text-xs h-12 px-8"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Send Inquiry <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
